import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Calendar, Globe, User } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalNewJoiners = () => {
  const [joiners, setJoiners] = useState([]);
  const [department, setDepartment] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/new-joiners`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJoiners(res.data.joiners || []);
        setDepartment(res.data.department || '');
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('portal_token');
          navigate('/employee-portal/login');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate]);

  const daysAgo = (dateStr) => {
    if (!dateStr) return '';
    const joined = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - joined) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return '1 day ago';
    if (diff < 30) return `${diff} days ago`;
    const months = Math.floor(diff / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-new-joiners">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
          <UserPlus className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">New Joiners</h2>
          <p className="text-xs text-gray-400">{department} Department &middot; Last 90 days</p>
        </div>
      </div>

      {joiners.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <UserPlus className="h-10 w-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No new joiners in the last 90 days</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {joiners.map(joiner => (
            <Card key={joiner.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`joiner-${joiner.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {joiner.photo_url ? (
                    <img src={`${API}${joiner.photo_url}`} alt="" className="w-11 h-11 rounded-full object-cover border border-gray-200 flex-shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-emerald-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-900 truncate">{joiner.name}</p>
                      <Badge className="bg-emerald-50 text-emerald-600 text-[9px] px-1.5 py-0 border-emerald-200">
                        {daysAgo(joiner.join_date)}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{joiner.position || 'N/A'}</p>
                    <div className="flex items-center gap-3 mt-2">
                      {joiner.join_date && (
                        <span className="flex items-center gap-1 text-[10px] text-gray-400">
                          <Calendar className="h-3 w-3" />
                          {new Date(joiner.join_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      {joiner.nationality && (
                        <span className="flex items-center gap-1 text-[10px] text-gray-400">
                          <Globe className="h-3 w-3" />
                          {joiner.nationality}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalNewJoiners;
